import React, { useEffect, useRef, useState } from 'react';
import { View, TouchableOpacity, StyleSheet, Animated, Easing, Dimensions, Platform } from 'react-native';
import { normalizeCardType, isCardType } from '@cubemeet/core';
import {
  MdPlace,
  MdRestaurant,
  MdEvent,
  MdHotel,
  MdDirectionsTransit,
  MdMoreHoriz,
  MdOndemandVideo,
  MdStickyNote2,
  MdAttachFile,
  MdShoppingCart,
  MdCode,
} from 'react-icons/md';

export interface QuickAddMenuProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (type: string) => void;
  showBottomRight?: boolean;
  /**
   * 是否显示行程表专用项目（视频、外部链接、其他）
   * 默认为 false（用于首页添加卡片）
   * 设为 true 时显示所有项目（用于行程表）
   */
  showItineraryItems?: boolean;
  /**
   * 可选的自定义图标渲染函数
   * 用于处理不同平台或模块的图标需求
   */
  renderIcon?: (key: string, iconName: string) => React.ReactNode;
}

const BUTTON_SIZE = 44;
// Keys removed from the radial quick-add menu by page-level customization.
// Exported so other modules can re-use the key/route for wiring elsewhere.
export const REMOVED_QUICKADD_KEYS: string[] = ['note'];

const items = [
  { key: 'poi', icon: 'place', label: '景点' },
  { key: 'meal', icon: 'restaurant', label: '餐饮' },
  { key: 'activity', icon: 'celebration', label: '活动' },
  { key: 'lodging', icon: 'hotel', label: '住宿' },
  { key: 'transport', icon: 'commute', label: '交通' },
  { key: 'shopping', icon: 'shopping_cart', label: '购物' },
  { key: 'video', icon: 'code', label: '嵌入' },
  // 将曲别针用于“外部链接”占位
  { key: 'file', icon: 'attach_file', label: '外部链接' },
  { key: 'other', icon: 'more_horiz', label: '其他' },
];

/**
 * 通用的快速添加菜单组件
 * 
 * 使用场景：
 * 1. AppLayout: 创建完整卡片模板
 * 2. ItineraryEditor: 插入占位符到时间轴
 * 
 * 组件只负责 UI 展示，具体的业务逻辑由调用方的 onSelect 回调处理
 */
const QuickAddMenu: React.FC<QuickAddMenuProps> = ({ 
  visible, 
  onClose, 
  onSelect, 
  renderIcon,
  showBottomRight = true,
  itineraryMode = true,
}) => {
  console.log('[QuickAddMenu] 组件渲染', { visible, itineraryMode });
  
  // 根据模式过滤图标：非行程表模式时移除交通、其他
  const filteredItems = itineraryMode 
    ? items 
    : items.filter(item => !['transport', 'other'].includes(item.key));
  
  const [mounted, setMounted] = useState<boolean>(visible);
  const animRefs = useRef(filteredItems.map(() => new Animated.Value(0))).current;
  const backdropAnim = useRef(new Animated.Value(0)).current;
  const rotateAnim = useRef(new Animated.Value(0)).current;
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      const { width, height } = Dimensions.get('window');
      setDimensions({ width, height });
    };
    updateDimensions();
    const subscription = Dimensions.addEventListener('change', updateDimensions);
    return () => subscription?.remove();
  }, []);

  useEffect(() => {
    const isWeb = Platform.OS === 'web';
    if (visible) {
      setMounted(true);
      Animated.loop(
        Animated.timing(rotateAnim, {
          toValue: 1,
          duration: 8000,
          easing: Easing.linear,
          useNativeDriver: !isWeb,
        })
      ).start();

      Animated.parallel([
        Animated.timing(backdropAnim, { toValue: 1, duration: 200, easing: Easing.out(Easing.cubic), useNativeDriver: !isWeb }),
        Animated.stagger(40, animRefs.map(a => Animated.timing(a, { toValue: 1, duration: 220, easing: Easing.out(Easing.cubic), useNativeDriver: !isWeb })))
      ]).start();
    } else if (mounted) {
      rotateAnim.stopAnimation();
      rotateAnim.setValue(0);
      Animated.parallel([
        Animated.timing(backdropAnim, { toValue: 0, duration: 160, easing: Easing.in(Easing.cubic), useNativeDriver: !isWeb }),
        Animated.stagger(20, animRefs.map(a => Animated.timing(a, { toValue: 0, duration: 160, easing: Easing.in(Easing.cubic), useNativeDriver: !isWeb })))
      ]).start(() => setMounted(false));
    }
  }, [visible]);

  if (!mounted || dimensions.width === 0) {
    console.log('[QuickAddMenu] 不渲染: mounted=', mounted, ', dimensions=', dimensions);
    return null;
  }

  const centerX = dimensions.width / 2;
  // Shift center down slightly to avoid header overlap (header height ~180px)
  const centerY = dimensions.height / 2 + 60;
  const radius = Math.min(dimensions.width, dimensions.height) * 0.3;

  console.log('[QuickAddMenu] 渲染菜单', { centerX, centerY, radius, itemsCount: filteredItems.length });

  const rotateInterpolate = rotateAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });

  const positions = filteredItems.map((_, i) => {
    const angle = (i * 2 * Math.PI) / filteredItems.length;
    const offsetX = Math.cos(angle) * radius;
    const offsetY = Math.sin(angle) * radius;
    return { offsetX, offsetY, angle };
  });

  // 默认图标渲染函数
  const defaultRenderIcon = (key: string): React.ReactNode => {
    switch (key) {
      case 'poi': return <MdPlace style={{ color: '#fff', fontSize: 18 }} />;
      case 'meal': return <MdRestaurant style={{ color: '#fff', fontSize: 18 }} />;
      case 'activity': return <MdEvent style={{ color: '#fff', fontSize: 18 }} />;
      case 'lodging': return <MdHotel style={{ color: '#fff', fontSize: 18 }} />;
      case 'transport': return <MdDirectionsTransit style={{ color: '#fff', fontSize: 18 }} />;
      case 'shopping': return <MdShoppingCart style={{ color: '#fff', fontSize: 18 }} />;
      case 'file': return <MdAttachFile style={{ color: '#fff', fontSize: 18 }} />;
      case 'video': return <MdCode style={{ color: '#fff', fontSize: 18 }} />;
      case 'note': return <MdStickyNote2 style={{ color: '#fff', fontSize: 18 }} />;
      case 'other': return <MdMoreHoriz style={{ color: '#fff', fontSize: 18 }} />;
      default: return <MdMoreHoriz style={{ color: '#fff', fontSize: 18 }} />;
    }
  };
// extra icons for floating actions (none required here)

  return (
    // Use style.pointerEvents instead of prop to avoid react-native-web deprecation warnings
    <View style={styles.overlay}>
      <Animated.View style={[styles.backdrop, { backgroundColor: 'rgba(0,0,0,0.32)', opacity: backdropAnim }]}>
        <TouchableOpacity 
          style={{ flex: 1, width: '100%', height: '100%' }} 
          activeOpacity={1} 
          onPress={(e) => {
            console.log('[QuickAddMenu] Backdrop 点击');
            onClose();
          }} 
        />
      </Animated.View>

  <View style={styles.centerContainer}>
        {filteredItems.map((it, i) => {
          const pos = positions[i];
          const { offsetX, offsetY } = pos;
          const ringColor = `rgba(${(i * 40) % 255}, ${(120 + i * 30) % 255}, ${(200 - i * 20) % 255}, 0.12)`;
          console.log('[QuickAddMenu] 渲染按钮', i, it.key, { offsetX, offsetY, centerX, centerY });
          
          const animatedStyle = Platform.OS === 'web' ? {
            position: 'absolute' as any,
            left: centerX - BUTTON_SIZE / 2 + offsetX,
            top: centerY - BUTTON_SIZE / 2 + offsetY,
            zIndex: 2147483647,
            pointerEvents: 'auto' as any,
          } : {
            position: 'absolute' as any,
            left: centerX - BUTTON_SIZE / 2,
            top: centerY - BUTTON_SIZE / 2,
            transform: [
              { translateX: offsetX },
              { translateY: offsetY },
              { rotate: rotateInterpolate },
              { scale: animRefs[i] }
            ],
            opacity: animRefs[i],
            zIndex: 2147483647,
            transformOrigin: 'center center' as any,
            pointerEvents: 'auto' as any,
          };
          
          return (
              <Animated.View
              key={it.key}
              style={animatedStyle}
            >
              <TouchableOpacity
                accessibilityLabel={`quickadd-${it.key}`}
                onPress={() => {
                  const normalized = normalizeCardType(it.key, { fallback: null });
                  const payload = normalized && isCardType(normalized) ? normalized : it.key;
                  onSelect(payload);
                  onClose();
                }}
                style={[styles.button, { borderColor: ringColor }]}
              >
                {renderIcon ? renderIcon(it.key, it.icon) : defaultRenderIcon(it.key)}
              </TouchableOpacity>
            </Animated.View>
          );
        })}
        {/* Bottom-right stacked icon buttons (moved from bottom center). Render only if requested */}
        { showBottomRight ? (
          <View style={styles.bottomRightContainer as any} pointerEvents="box-none">
            <View style={styles.stackColumn as any}>
              <TouchableOpacity
                accessibilityLabel="quickaction-map"
                onPress={() => { console.log('[QuickAddMenu] quickaction map clicked'); onSelect && onSelect('map'); onClose && onClose(); }}
                style={[styles.smallButton, { backgroundColor: '#FB7A4A' }]}
              >
                <MdPlace style={{ color: '#fff', fontSize: 20 }} />
              </TouchableOpacity>
              <TouchableOpacity
                accessibilityLabel="quickaction-publish"
                onPress={() => { console.log('[QuickAddMenu] quickaction publish clicked'); onSelect && onSelect('publish'); onClose && onClose(); }}
                style={[styles.smallButton, { backgroundColor: '#2563EB', marginTop: 12 }]}
              >
                <MdAttachFile style={{ color: '#fff', fontSize: 18 }} />
              </TouchableOpacity>
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...Platform.select({
      web: {
        position: 'fixed' as any,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: 2147483647,
      },
      default: {
        position: 'absolute' as any,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    }),
    pointerEvents: 'box-none'
  },
  backdrop: {
    ...Platform.select({
      web: {
        position: 'fixed' as any,
      },
      default: {
        position: 'absolute' as any,
      },
    }),
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 2147483640,
    width: '100%',
    height: '100%',
  },
  centerContainer: {
    ...Platform.select({
      web: {
        position: 'fixed' as any,
      },
      default: {
        position: 'absolute' as any,
      },
    }),
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    // box-none 让点击穿透到 backdrop
    pointerEvents: 'box-none',
    zIndex: 2147483645,
  },
  button: {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: BUTTON_SIZE / 2,
    backgroundColor: '#FB7A4A',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: { 
        boxShadow: '0px 6px 20px rgba(0,0,0,0.18)',
        cursor: 'pointer',
        display: 'flex',
      },
      default: { elevation: 18, shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 8 },
    }),
    borderWidth: 2,
    pointerEvents: 'auto',
  },
  bottomRightContainer: {
    position: 'fixed' as any,
    right: 20,
    bottom: 96,
    zIndex: 2147483650,
    pointerEvents: 'box-none',
  },
  stackColumn: {
    display: 'flex',
    flexDirection: 'column' as any,
    alignItems: 'center',
  },
  smallButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { boxShadow: '0px 6px 18px rgba(0,0,0,0.16)', cursor: 'pointer', display: 'flex' }, default: { elevation: 12 } }),
  },
});

export default QuickAddMenu;